import React from "react";
import PropTypes from "prop-types";
import ReactGA from "react-ga";
import Autobind from "autobind-decorator";
import { inject, observer } from "mobx-react";
import {
  generateSarcopeniaReportPdf,
  getDicomPatientDefaults,
} from "component/util/sarcopeniaReport";
import pubsub from "core/global/global";
import CustomEvent from "core/customEvent/index";
import "./reportPanel.css";

// Auto 화면 Report 카드의 Generate Report 아이콘 (Sarcopenia_Auto_Frontend/src/asset/image/generateReport.ts)
const ReportIcon = () => (
  <svg width="20" height="22" viewBox="0 0 27.183 30.558" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M25.251,30.558H1.951a1.908,1.908,0,0,1-1.365-.552,1.806,1.806,0,0,1-.576-1.358V1.94A1.808,1.808,0,0,1,.585.581,1.9,1.9,0,0,1,1.951.03H15.542v10.5a.91.91,0,0,0,.288.671.941.941,0,0,0,.683.284H27.192V28.648a1.832,1.832,0,0,1-.561,1.358A1.888,1.888,0,0,1,25.251,30.558Zm-2.913-5.73h-.971V18.144a.91.91,0,0,0-.288-.671.941.941,0,0,0-.683-.284H18.455a.941.941,0,0,0-.683.284.91.91,0,0,0-.288.671v6.684H15.542v-10.5a.91.91,0,0,0-.288-.671.941.941,0,0,0-.683-.284H12.63a.941.941,0,0,0-.683.284.91.91,0,0,0-.288.671v10.5H9.717V20.053a.91.91,0,0,0-.288-.671.941.941,0,0,0-.683-.284H6.8a.941.941,0,0,0-.683.284.91.91,0,0,0-.288.671v4.775H4.863a.941.941,0,0,0-.683.284.926.926,0,0,0,0,1.343.941.941,0,0,0,.683.284H22.338a.95.95,0,0,0,.971-.955.915.915,0,0,0-.288-.671A.939.939,0,0,0,22.338,24.828ZM17.484,0a1.832,1.832,0,0,1,1.335.537l7.8,7.669a1.854,1.854,0,0,1,.576,1.373H17.484Z"
      transform="translate(-0.009)"
      fill="#06abd8"
    />
  </svg>
);

/**
 * @description PDF 리포트 입력 카드. Auto 화면의 Report 카드(Gender / Height / Age / Weight + Generate Report)와 같은 모양.
 *              예전 PDF Report 버튼 + 입력 다이얼로그(reportDialog)를 대신한다.
 */
@inject("sarcopeniaMeasureStore")
@observer
@Autobind
class ReportPanel extends React.Component {
  static propTypes = {
    disabled: PropTypes.bool,
  };
  static defaultProps = {
    disabled: false,
  };

  state = {
    sex: "M",
    height: "0",
    age: "0",
    weight: "0",
    error: "",
    busy: false,
  };

  // 분석 결과가 나오면 지금 열린 DICOM 의 나이(PatientAge)/성별(PatientSex)로 채운다.
  // 같은 파일에서는 한 번만 채우고(사용자가 고친 값 유지), 다른 파일을 열면 그 파일 값으로 다시 채운다.
  // (이전: 처음 한 번만 채우고 나이가 0 일 때만 바꿔서, 파일을 바꿔도 첫 파일 나이가 계속 남았다)
  // defaultsApplied = false;
  appliedKey = null;

  // componentDidUpdate() {
  //   const hasResult = this.props.sarcopeniaMeasureStore._measureDatalist.length > 0;
  //   if (!hasResult) return;
  //   ...
  // }
  // → 새 DICOM 을 올리는 순간(DELIVER_DATA_TO_VIEWPORT) 바로 채운다. 분석 결과가 나올 때도 한 번 더 확인.

  componentDidMount() {
    this.loadSubId = pubsub.subscribe(CustomEvent.Messages.DELIVER_DATA_TO_VIEWPORT, this.handleDataLoaded);
  }

  componentWillUnmount() {
    if (this.loadSubId != null) pubsub.unsubscribe(this.loadSubId);
    clearTimeout(this.loadTimer);
  }

  componentDidUpdate() {
    this.syncFromDicom();
  }

  handleDataLoaded() {
    // 시리즈 정보가 캐시에 다 들어간 뒤 읽도록 조금 늦춘다
    clearTimeout(this.loadTimer);
    this.loadTimer = setTimeout(this.syncFromDicom, 300);
  }

  // 다른 DICOM 이 열리면: 성별·나이는 DICOM 값, 키·몸무게는 0 (직접 입력). 이름은 PDF 만들 때 DICOM 에서 읽는다
  syncFromDicom() {
    const d = getDicomPatientDefaults();
    if (!d.key || this.appliedKey === d.key) return;
    this.appliedKey = d.key;
    this.setState({
      // 분석 전에 눌러서 남아 있는 "Run the area measurement first." 도 지운다
      error: "",
      sex: d.sex || "M",
      age: d.age ? String(d.age) : "0",
      height: "0",
      weight: "0",
    });
  }

  // 숫자와 소수점만 (Auto 화면과 같은 규칙)
  handleChange(field) {
    return (e) => {
      const value = e.target.value;
      if (!/^(\d+)?(\.\d*)?$/.test(value)) return;
      this.setState({ [field]: value, error: "" });
    };
  }

  // 0 이 들어 있는 칸을 누르면 비운다 (Auto 화면과 같음)
  handleFocus(field) {
    return () => {
      if (this.state[field] === "0") this.setState({ [field]: "" });
    };
  }

  handleBlur(field) {
    return () => {
      if (this.state[field] === "") this.setState({ [field]: "0" });
    };
  }

  setSex(sex) {
    this.setState({ sex, error: "" });
  }

  validate() {
    const age = Number(this.state.age);
    const heightCm = Number(this.state.height);
    const weightKg = Number(this.state.weight);
    if (!heightCm || heightCm < 50 || heightCm > 250) return { error: "Height: 50–250 cm" };
    if (!age || age < 1 || age > 130) return { error: "Age: 1–130" };
    if (!weightKg || weightKg < 10 || weightKg > 300) return { error: "Weight: 10–300 kg" };
    // return { patient: { name: "Anonymized", age, sex: this.state.sex, heightCm, weightKg } };
    // 이름: 지금 열린 DICOM 의 PatientName, 없으면 Anonymized
    const name = getDicomPatientDefaults().name || "Anonymized";
    return { patient: { name, age, sex: this.state.sex, heightCm, weightKg } };
  }

  async handleSubmit(e) {
    e.preventDefault();
    const { disabled, sarcopeniaMeasureStore } = this.props;
    const hasResult = sarcopeniaMeasureStore._measureDatalist.length > 0;
    if (disabled || this.state.busy) return;
    if (!hasResult) {
      this.setState({ error: "Run the area measurement first." });
      return;
    }
    const { error, patient } = this.validate();
    if (error) {
      this.setState({ error });
      return;
    }

    ReactGA.event({ category: "Export", action: "Download PDF report" });
    this.setState({ busy: true });
    // "Creating..." 가 먼저 그려지도록 한 프레임 양보
    await new Promise((resolve) => setTimeout(resolve, 30));
    try {
      generateSarcopeniaReportPdf(sarcopeniaMeasureStore, patient);
      this.setState({ busy: false });
    } catch (err) {
      this.setState({ busy: false, error: (err && err.message) || "Failed to create the PDF." });
    }
  }

  // 뷰어의 단축키·드래그 처리로 입력이 넘어가지 않게
  stop(e) {
    e.stopPropagation();
  }

  renderInput(field, extraClass = "") {
    return (
      <input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        className={`report-panel__input ${extraClass}`}
        value={this.state[field]}
        onChange={this.handleChange(field)}
        onFocus={this.handleFocus(field)}
        onBlur={this.handleBlur(field)}
        onKeyDown={this.stop}
        disabled={this.props.disabled}
      />
    );
  }

  render() {
    const { disabled, sarcopeniaMeasureStore } = this.props;
    const { sex, busy, error } = this.state;
    const hasResult = sarcopeniaMeasureStore._measureDatalist.length > 0;
    const inactive = disabled || !hasResult || busy;

    return (
      <form className="report-panel" onSubmit={this.handleSubmit} onMouseDown={this.stop}>
        <label className="item-title">{"Report"}</label>
        <div className="report-panel__grid">
          <div className="report-panel__cell">
            <span className="report-panel__label">Gender</span>
            <span className="report-panel__gender">
              <button
                type="button"
                className={`report-panel__sex${sex === "M" ? " is-active" : ""}`}
                onClick={() => this.setSex("M")}
                title="Male"
                aria-pressed={sex === "M"}
              >
                ♂
              </button>
              <button
                type="button"
                className={`report-panel__sex${sex === "F" ? " is-active" : ""}`}
                onClick={() => this.setSex("F")}
                title="Female"
                aria-pressed={sex === "F"}
              >
                ♀
              </button>
            </span>
          </div>
          <div className="report-panel__cell">
            <span className="report-panel__label">Height</span>
            {this.renderInput("height")}
            <span className="report-panel__unit">cm</span>
          </div>
          <div className="report-panel__cell">
            <span className="report-panel__label">Age</span>
            {this.renderInput("age")}
            <span className="report-panel__unit" />
          </div>
          <div className="report-panel__cell">
            <span className="report-panel__label">Weight</span>
            {this.renderInput("weight")}
            <span className="report-panel__unit">kg</span>
          </div>
        </div>
        <button
          type="submit"
          className={`report-panel__generate${inactive ? " is-inactive" : ""}`}
          disabled={disabled || busy}
          title={hasResult ? "" : "Run the area measurement first."}
        >
          <span className="report-panel__icon">
            <ReportIcon />
          </span>
          {busy ? "Creating..." : "Generate Report"}
        </button>
        <div className="report-panel__error">{error}</div>
      </form>
    );
  }
}

export default ReportPanel;
